import {
  chatApi,
  ChatAudioTranscriptionResponse,
  MaposOrdersResponse,
  SupportTicketStatus,
  SupportTicketsResponse,
  ChatSendResponse,
  TestChatContextModel,
  TestChatMetadataPayload,
  TestChatReplayBatchResult,
} from "../api/ChatApi";
import { configApi } from "../api/ConfigApi";
import { ChatModel } from "../models/Chat/ChatModel";
import { ChatParams } from "../models/Chat/ChatParams";
import { MessageModel, MessageTypeEnum } from "../models/Chat/MessageModel";
import { MessageParams } from "../models/Chat/MessageParams";

class ChatManager {
  private static instance: ChatManager;

  public static get(): ChatManager {
    if (!this.instance) {
      this.instance = new ChatManager();
    }
    return this.instance;
  }

  private maskAccessToken(value?: string): string {
    if (!value) {
      return "";
    }

    if (value.length <= 8) {
      return "Configurado";
    }

    return `${value.slice(0, 4)}...${value.slice(-4)}`;
  }

  async getAllChats(): Promise<ChatModel[] | undefined> {
    try {
      const result = await chatApi.getAllChats.request(null);
      return (
        result.data?.map((chat: Partial<ChatModel>) => new ChatModel(chat)) ??
        []
      );
    } catch (error) {
      console.error("Error en getAllChats:", error);
      return [];
    }
  }

  async getChat(phoneNumber: string): Promise<MessageModel[] | undefined> {
    try {
      const params: ChatParams = {
        phoneNumber,
      };
      const result = await chatApi.getChat.request(params);
      return (
        result.data?.map(
          (chat: Partial<MessageModel>) => new MessageModel(chat)
        ) ?? []
      );
    } catch (error) {
      console.error("Error en getChat:", error);
      return [];
    }
  }

  async getMaposOrders({
    phoneNumber,
    orderId,
    limit = 5,
  }: {
    phoneNumber: string;
    orderId?: string;
    limit?: number;
  }): Promise<MaposOrdersResponse> {
    const params: { phoneNumber: string; orderId?: string; limit: number } = {
      phoneNumber,
      limit,
    };
    const normalizedOrderId = orderId?.trim();
    if (normalizedOrderId) params.orderId = normalizedOrderId;
    const result = await chatApi.getMaposOrders.requestHard(params);
    if (!result.isSuccess || !result.data || result.data.error) {
      throw new Error(result.data?.error || result.messageError || "No se pudieron consultar los pedidos.");
    }
    return result.data;
  }

  async resolveSupportTicket(ticketCode: string): Promise<boolean> {
    const result = await chatApi.resolveSupportTicket.requestHard({ ticketCode });
    return Boolean(result.isSuccess && result.data?.resolved);
  }

  async getSupportTickets(params: {
    status?: SupportTicketStatus;
    phoneNumber?: string;
    ticketCode?: string;
    before?: string;
    limit?: number;
  } = {}): Promise<SupportTicketsResponse> {
    const result = await chatApi.getSupportTickets.requestHard({
      ...params,
      limit: params.limit || 50,
    });
    if (!result.isSuccess || !result.data || result.data.error) {
      throw new Error(result.data?.error || result.messageError || "No se pudieron consultar los tickets de soporte.");
    }
    return result.data;
  }

  async updateSupportTicketStatus(
    ticketCode: string,
    status: SupportTicketStatus
  ): Promise<boolean> {
    const result = await chatApi.updateSupportTicketStatus.requestHard({ ticketCode, status });
    return Boolean(result.isSuccess && result.data?.updated);
  }

  async updateChat(phoneNumber: string): Promise<boolean> {
    try {
      const params: ChatParams = {
        phoneNumber,
      };
      const result = await chatApi.updateChat.request(params);
      return result.isSuccess;
    } catch (error) {
      console.error("Error en getChat:", error);
      return false;
    }
  }

  async sendMessage({
    phoneNumber,
    type,
    message,
    clientMessageId,
    replyToMessageId,
    replyToSender,
    replyToRole,
    replyPreview,
  }: {
    phoneNumber: string;
    type: MessageTypeEnum;
    message: string;
    clientMessageId?: string;
    replyToMessageId?: string;
    replyToSender?: string;
    replyToRole?: string;
    replyPreview?: string;
  }): Promise<ChatSendResponse> {
    try {
      const params: MessageParams = {
        phoneNumber,
        message,
        type,
        clientMessageId,
        replyToMessageId,
        replyToSender,
        replyToRole,
        replyPreview,
      };
      const result = await chatApi.sendMessage.request(params);
      if (!result.isSuccess) {
        return {
          sent: false,
          errorMessage: result.messageError || "No se pudo enviar el mensaje.",
        };
      }
      return result.data || { sent: false, errorMessage: "No hubo respuesta del API." };
    } catch (error) {
      console.error("Error en sendMessage:", error);
      return {
        sent: false,
        errorMessage: "No se pudo enviar el mensaje.",
      };
    }
  }

  async updateContact({
    phoneNumber,
    customName,
    profilePictureUrl,
    notes,
    tags,
  }: {
    phoneNumber: string;
    customName?: string;
    profilePictureUrl?: string;
    notes?: string;
    tags?: string[];
  }): Promise<boolean> {
    try {
      const result = await chatApi.updateContact.requestHard({
        phoneNumber,
        customName,
        profilePictureUrl,
        notes,
        tags,
      });
      return Boolean(result.isSuccess && result.data?.updated);
    } catch (error) {
      console.error("Error en updateContact:", error);
      return false;
    }
  }

  async sendTyping(messageId: string): Promise<boolean> {
    try {
      const result = await chatApi.sendTyping.request({ messageId });
      return Boolean(result.isSuccess && result.data?.sent);
    } catch (error) {
      console.error("Error en sendTyping:", error);
      return false;
    }
  }

  async transcribeAudioMessage({
    phoneNumber,
    messageId,
  }: {
    phoneNumber: string;
    messageId: string;
  }): Promise<ChatAudioTranscriptionResponse | undefined> {
    try {
      const result = await chatApi.transcribeAudioMessage.request({
        phoneNumber,
        messageId,
      });
      if (!result.isSuccess) {
        throw new Error(
          result.messageError || "No se pudo transcribir el audio."
        );
      }
      return result.data;
    } catch (error) {
      console.error("Error en transcribeAudioMessage:", error);
      throw error;
    }
  }

  async sendTestMessage({
    type,
    message,
  }: {
    type: MessageTypeEnum;
    message: string;
  }): Promise<boolean | undefined> {
    try {
      const result = await chatApi.sendTestMessage.request({
        message,
        type,
      });
      return result.isSuccess;
    } catch (error) {
      console.error("Error en sendTestMessage:", error);
      return undefined;
    }
  }

  async getTestChatContext(): Promise<TestChatContextModel | undefined> {
    try {
      const result = await chatApi.getTestChatContext.request(null);
      if (result.isSuccess && result.data) {
        return result.data;
      }

      const fallbackResult = await configApi.allConfigInfo.request(null);
      if (fallbackResult.isSuccess && fallbackResult.data) {
        return {
          companyId: fallbackResult.data.aiSettings?.companyId || "",
          wsSettings: {
            phoneNumberId: fallbackResult.data.wsSettings?.phoneNumberId || "",
            whatsappId: fallbackResult.data.wsSettings?.whatsappId || "",
            accessTokenMasked: this.maskAccessToken(
              fallbackResult.data.wsSettings?.accessToken
            ),
          },
        };
      }

      throw new Error(
        result.messageError ||
          fallbackResult.messageError ||
          "No se pudo cargar la configuración."
      );
    } catch (error) {
      console.error("Error en getTestChatContext:", error);
      throw error;
    }
  }

  async resetTestChat(deleteHistory: boolean = false): Promise<boolean> {
    try {
      const result = await chatApi.resetTestChat.request({ deleteHistory });
      return result.isSuccess;
    } catch (error) {
      console.error("Error en resetTestChat:", error);
      return false;
    }
  }

  async updateTestSessionMetadata({
    serviceCode,
    metadata,
  }: {
    serviceCode: string;
    metadata: TestChatMetadataPayload;
  }): Promise<boolean> {
    try {
      const result = await chatApi.updateTestSessionMetadata.request({
        serviceCode,
        metadata,
      });
      return result.isSuccess;
    } catch (error) {
      console.error("Error en updateTestSessionMetadata:", error);
      return false;
    }
  }

  async replayTestSession({
    serviceCode,
    iterations,
  }: {
    serviceCode: string;
    iterations?: number;
  }): Promise<{
    data?: TestChatReplayBatchResult;
    error?: string;
  }> {
    try {
      const result = await chatApi.replayTestSession.requestHard({
        serviceCode,
        iterations,
      });
      return result.isSuccess
        ? { data: result.data }
        : { error: result.messageError || "No se pudo ejecutar el replay." };
    } catch (error: any) {
      console.error("Error en replayTestSession:", error);
      return {
        error: error?.message || "No se pudo ejecutar el replay.",
      };
    }
  }
}

export const chatManager = ChatManager.get();
